import { useFlags } from "launchdarkly-react-client-sdk";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useSelector } from "react-redux";

import LoadingButton from "@mui/lab/LoadingButton";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import Badge from "@mui/material/Badge";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import useTheme from "@mui/material/styles/useTheme";

import isEqual from "lodash/isEqual";

import { useAppDispatch } from "app/hooks";
import { neutral } from "app/themes/colors";
import CheckboxFormField from "components/checkbox-form-field";
import Dialog from "components/dialog";
import SelectFormField from "components/select-form-field";
import { INVOICE_TABLE_FONT_SIZE_FEATURE } from "constants/feature-flags";

import { getConfigurableLineItemsHeaders } from "../merge-tags/merge-tags-values/common/configurable-merge-tags";
import {
  AUTO_TABLE_FONT_SIZE_VALUE,
  MAX_TABLE_FONT_SIZE_PX,
  MIN_TABLE_FONT_SIZE_PX,
  STANDARD_TOTALS_FONT_SIZE_PX,
  STANDARD_TOTALS_FONT_SIZE_VALUE,
  TABLE_TOTALS_FONT_SIZE_VALUE,
  getAutoTableFontSizePx,
  getMaxExplicitTableFontSizePx,
  parseTableFontSizePx
} from "../merge-tags/merge-tags-values/common/table-font-size.utils";
import { selectDataParamsById, setDataParams } from "../word-processor.slice";
import DeviceSettings from "./components/device-settings.component";
import GeneralSettings from "./components/general-settings.component";
import ServicesSettings from "./components/services-settings.component";
import TotalsSettings from "./components/totals-settings.component";
import {
  type InvoiceTableTagRequest,
  formValuesFromTagOptions,
  tagOptionsFromFormValues
} from "./invoice-table-tag-options.utils";
import { createDefaultValues, filterConfigurationSections } from "./utils";

export interface SelectSaleConfigurationValues {
  // general
  include_hearing_aids: boolean;
  include_domes: boolean;
  include_receivers: boolean;
  include_tubes: boolean;
  include_earmolds: boolean;
  include_batteries: boolean;
  include_chargers: boolean;
  include_remotes: boolean;
  include_others: boolean;
  include_services_dispensing_fees: boolean;
  include_totals: boolean;

  //devices
  hearing_aids_show_product: boolean;
  hearing_aids_show_serial: boolean;
  hearing_aids_show_manufacturer: boolean;
  hearing_aids_show_model: boolean;
  hearing_aids_show_name: boolean;
  hearing_aids_show_color: boolean;
  hearing_aids_show_battery: boolean;
  hearing_aids_show_mfr_warranty_exp: boolean;
  hearing_aids_show_lnd_warranty_exp: boolean;
  hearing_aids_show_service_plan_exp: boolean;
  hearing_aids_show_lnd_deductible: boolean;
  hearing_aids_show_price: boolean;
  hearing_aids_show_quantity: boolean;
  hearing_aids_show_patient_resp: boolean;
  hearing_aids_show_insurance_resp: boolean;
  hearing_aids_show_managed_care_resp: boolean;
  hearing_aids_show_cpt_code: boolean;
  hearing_aids_show_ear: boolean;

  domes_show_product: boolean;
  domes_show_serial: boolean;
  domes_show_manufacturer: boolean;
  domes_show_model: boolean;
  domes_show_name: boolean;
  domes_show_mfr_warranty_exp: boolean;
  domes_show_lnd_warranty_exp: boolean;
  domes_show_lnd_deductible: boolean;
  domes_show_price: boolean;
  domes_show_quantity: boolean;
  domes_show_patient_resp: boolean;
  domes_show_insurance_resp: boolean;
  domes_show_managed_care_resp: boolean;
  domes_show_cpt_code: boolean;
  domes_show_dome_type: boolean;
  domes_show_dome_size: boolean;
  domes_show_ear: boolean;

  receivers_show_product: boolean;
  receivers_show_manufacturer: boolean;
  receivers_show_model: boolean;
  receivers_show_name: boolean;
  receivers_show_color: boolean;
  receivers_show_serial: boolean;
  receivers_show_mfr_warranty_exp: boolean;
  receivers_show_lnd_warranty_exp: boolean;
  receivers_show_lnd_deductible: boolean;
  receivers_show_price: boolean;
  receivers_show_quantity: boolean;
  receivers_show_patient_resp: boolean;
  receivers_show_insurance_resp: boolean;
  receivers_show_managed_care_resp: boolean;
  receivers_show_cpt_code: boolean;
  receivers_show_gain: boolean;
  receivers_show_length: boolean;
  receivers_show_ear: boolean;

  tubes_show_product: boolean;
  tubes_show_manufacturer: boolean;
  tubes_show_model: boolean;
  tubes_show_name: boolean;
  tubes_show_serial: boolean;
  tubes_show_mfr_warranty_exp: boolean;
  tubes_show_lnd_warranty_exp: boolean;
  tubes_show_lnd_deductible: boolean;
  tubes_show_price: boolean;
  tubes_show_quantity: boolean;
  tubes_show_patient_resp: boolean;
  tubes_show_insurance_resp: boolean;
  tubes_show_managed_care_resp: boolean;
  tubes_show_cpt_code: boolean;
  tubes_show_ear: boolean;

  earmolds_show_product: boolean;
  earmolds_show_manufacturer: boolean;
  earmolds_show_model: boolean;
  earmolds_show_name: boolean;
  earmolds_show_serial: boolean;
  earmolds_show_mfr_warranty_exp: boolean;
  earmolds_show_lnd_warranty_exp: boolean;
  earmolds_show_lnd_deductible: boolean;
  earmolds_show_price: boolean;
  earmolds_show_quantity: boolean;
  earmolds_show_patient_resp: boolean;
  earmolds_show_insurance_resp: boolean;
  earmolds_show_managed_care_resp: boolean;
  earmolds_show_cpt_code: boolean;
  earmolds_show_material: boolean;
  earmolds_show_canal_length: boolean;
  earmolds_show_canal_venting: boolean;
  earmolds_show_ear: boolean;

  batteries_show_product: boolean;
  batteries_show_manufacturer: boolean;
  batteries_show_model: boolean;
  batteries_show_name: boolean;
  batteries_show_color: boolean;
  batteries_show_serial: boolean;
  batteries_show_mfr_warranty_exp: boolean;
  batteries_show_lnd_warranty_exp: boolean;
  batteries_show_lnd_deductible: boolean;
  batteries_show_price: boolean;
  batteries_show_quantity: boolean;
  batteries_show_patient_resp: boolean;
  batteries_show_insurance_resp: boolean;
  batteries_show_managed_care_resp: boolean;
  batteries_show_cpt_code: boolean;

  //chargers
  chargers_show_product: boolean;
  chargers_show_manufacturer: boolean;
  chargers_show_model: boolean;
  chargers_show_name: boolean;
  chargers_show_serial: boolean;
  chargers_show_mfr_warranty_exp: boolean;
  chargers_show_lnd_warranty_exp: boolean;
  chargers_show_lnd_deductible: boolean;
  chargers_show_price: boolean;
  chargers_show_quantity: boolean;
  chargers_show_patient_resp: boolean;
  chargers_show_insurance_resp: boolean;
  chargers_show_managed_care_resp: boolean;
  chargers_show_cpt_code: boolean;

  remotes_show_product: boolean;
  remotes_show_manufacturer: boolean;
  remotes_show_model: boolean;
  remotes_show_name: boolean;
  remotes_show_serial: boolean;
  remotes_show_mfr_warranty_exp: boolean;
  remotes_show_lnd_warranty_exp: boolean;
  remotes_show_lnd_deductible: boolean;
  remotes_show_price: boolean;
  remotes_show_quantity: boolean;
  remotes_show_patient_resp: boolean;
  remotes_show_insurance_resp: boolean;
  remotes_show_managed_care_resp: boolean;
  remotes_show_cpt_code: boolean;

  others_show_product: boolean;
  others_show_manufacturer: boolean;
  others_show_model: boolean;
  others_show_name: boolean;
  others_show_serial: boolean;
  others_show_mfr_warranty_exp: boolean;
  others_show_lnd_warranty_exp: boolean;
  others_show_lnd_deductible: boolean;
  others_show_price: boolean;
  others_show_quantity: boolean;
  others_show_patient_resp: boolean;
  others_show_insurance_resp: boolean;
  others_show_managed_care_resp: boolean;
  others_show_cpt_code: boolean;

  //services

  services_show_cpt_code: boolean;
  services_show_product: boolean;
  services_show_description: boolean;
  services_show_price: boolean;
  services_show_quantity: boolean;

  //totals
  total_show_services_dispensing_fees: boolean;
  total_show_discounts: boolean;
  total_tax: boolean;
  total_amount: boolean;
  total_patient_payments: boolean;
  total_insurance_payments: boolean;
  amount_due_from_insurance: boolean;
  amount_due_from_patient: boolean;
  balance_due: boolean;
  total_show_cost_of_ha: boolean;

  // appearance — "auto" or a px value ("8".."16"); serialized into the merge
  // tag options string as font_size=<px> (omitted when "auto")
  font_size: string;
  // "standard" (14px, omitted from the tag), "table", or a px value ("8".."16"); serialized as
  // totals_font_size=<value> (ENG-2462)
  totals_font_size: string;
  // ENG-2487: head the Name column "Model" and print it after Manufacturer; serialized as name_as_model=yes
  name_as_model: boolean;
}

const TABLE_FONT_SIZE_OPTIONS = [
  { value: AUTO_TABLE_FONT_SIZE_VALUE, label: "Auto (fits column count)" },
  ...Array.from(
    { length: MAX_TABLE_FONT_SIZE_PX - MIN_TABLE_FONT_SIZE_PX + 1 },
    (_, index) => {
      const px = MIN_TABLE_FONT_SIZE_PX + index;
      return { value: String(px), label: `${px} px` };
    }
  )
];

const TOTALS_FONT_SIZE_OPTIONS = [
  {
    value: STANDARD_TOTALS_FONT_SIZE_VALUE,
    label: `Standard (${STANDARD_TOTALS_FONT_SIZE_PX} px)`
  },
  { value: TABLE_TOTALS_FONT_SIZE_VALUE, label: "Same as the table" },
  ...TABLE_FONT_SIZE_OPTIONS.filter(
    (option) => option.value !== AUTO_TABLE_FONT_SIZE_VALUE
  )
];

const TOTALS_FONT_SIZE_HELPER_TEXT: Record<string, string> = {
  [STANDARD_TOTALS_FONT_SIZE_VALUE]: `Totals print at ${STANDARD_TOTALS_FONT_SIZE_PX}px, the size they have always printed at.`,
  [TABLE_TOTALS_FONT_SIZE_VALUE]:
    "Totals print at the same size as the line items."
};

interface WordProcessorSelectSaleConfigurationDialogProps {
  readonly id: string;
  /**
   * An invoice table tag being inserted or edited (INVOICE_TABLE_TAG_EDIT_FEATURE): its options fill the dialog,
   * and Apply hands the new options to `onTagApply` as well as saving them as the editor's configuration. Absent,
   * the dialog only saves the configuration, as it always has.
   */
  readonly tagRequest?: InvoiceTableTagRequest | null;
  readonly onTagApply?: (options: Record<string, any>) => void;
  readonly onTagRemove?: () => void;
  /** The dialog closed, whichever way: the request is over. */
  readonly onTagDone?: () => void;
}

const TAG_TITLES = {
  insert: "Insert Invoice Table",
  edit: "Edit Invoice Table"
};

const TAG_APPLY_LABELS = {
  insert: "Insert Table",
  edit: "Update Table"
};

const TAG_INTROS = {
  insert:
    "Choose the items and columns the table prints. It fills in from the patient's invoice when a document is made from this template. To change it later, click the table in the template.",
  edit: "Change the items and columns this table prints, then Update Table. Only this table changes: other tables and other templates keep their own settings. Nothing is saved until you save the template."
};

export default function WordProcessorSelectSaleConfigurationDialog({
  id,
  tagRequest,
  onTagApply,
  onTagRemove,
  onTagDone
}: WordProcessorSelectSaleConfigurationDialogProps) {
  const { zIndex } = useTheme();
  const dispatch = useAppDispatch();
  const flags = useFlags();
  // Kill switch, default visible: the font UI already shipped, so an absent
  // flag keeps it. Off hides the picker AND stops persisting font_size.
  const isTableFontSizeEnabled: boolean =
    flags[INVOICE_TABLE_FONT_SIZE_FEATURE] ?? true;
  const dataParams =
    useSelector((state: any) => selectDataParamsById(state, id)) || {};

  const defaultValues: SelectSaleConfigurationValues = useMemo(() => {
    const data = (dataParams?.saleConfiguration ||
      {}) as SelectSaleConfigurationValues;
    return createDefaultValues(data);
  }, [dataParams?.saleConfiguration]);

  const formMethods = useForm<SelectSaleConfigurationValues>({
    mode: "onBlur",
    defaultValues
  });
  // What the form started with: a tag's options replace the form's defaults while it is open, and closing puts
  // these back, so the next plain Define Invoice Parameters opens exactly as it did before.
  const initialValuesRef = useRef(defaultValues);

  const {
    handleSubmit,
    reset,
    watch,
    formState: { isValid }
  } = formMethods;

  const allValues = watch();

  const isOpen = Boolean(id && dataParams.openSelectSaleConfigurationDialog);
  const tagMode = tagRequest?.mode;
  // A tag's options are read with a missing option as off (formValuesFromTagOptions), not with the dialog's
  // own defaults, which would switch back on every column the tag left out. A layout effect, so the dialog never
  // paints a frame with the defaults' columns first. The values are kept to tell an unchanged Update.
  const tagValuesRef = useRef<SelectSaleConfigurationValues | null>(null);
  useLayoutEffect(() => {
    if (isOpen && tagRequest) {
      const values = formValuesFromTagOptions(tagRequest.options);
      tagValuesRef.current = values;
      reset(values);
    }
  }, [isOpen, tagRequest, reset]);

  // Derived from the same section-filtered view handleSave persists, so the
  // chips and the font-size ceiling always describe the table that renders.
  const filteredConfiguration = useMemo(
    () => filterConfigurationSections(allValues),
    [allValues]
  );
  // ENG-2487: the "headed Model" choice applies only while some section prints a Name.
  const hasNameColumn = Object.keys(filteredConfiguration).some((key) =>
    key.endsWith("_show_name")
  );
  const tableColumns = useMemo(() => {
    return getConfigurableLineItemsHeaders(filteredConfiguration);
  }, [filteredConfiguration]);

  // The largest size that keeps every value inside its cell for the
  // configured column count. The picker only offers sizes up to this, so
  // whatever is chosen here is exactly what renders — the table never
  // second-guesses the choice.
  const maxFontSizePx = getMaxExplicitTableFontSizePx(tableColumns.length);

  const fontSizeOptions = useMemo(
    () =>
      TABLE_FONT_SIZE_OPTIONS.map((option) =>
        option.value === AUTO_TABLE_FONT_SIZE_VALUE
          ? option
          : { ...option, disabled: Number(option.value) > maxFontSizePx }
      ),
    [maxFontSizePx]
  );

  // Adding columns can invalidate the current pick; snap it down in the
  // visible control (never silently at render time). Uses
  // formMethods.setValue directly — `setValue` is taken by the tab state.
  useEffect(() => {
    if (!isTableFontSizeEnabled) return;
    const explicitPx = parseTableFontSizePx(allValues.font_size);
    if (explicitPx !== undefined && explicitPx > maxFontSizePx) {
      formMethods.setValue("font_size", String(maxFontSizePx), {
        shouldValidate: false
      });
    }
  }, [allValues.font_size, maxFontSizePx, formMethods, isTableFontSizeEnabled]);

  const fontSizeHelperText = useMemo(() => {
    const columnCount = tableColumns.length;
    const autoPx = getAutoTableFontSizePx(columnCount);
    const explicitPx = parseTableFontSizePx(allValues.font_size);

    const capNote =
      maxFontSizePx < MAX_TABLE_FONT_SIZE_PX
        ? ` Sizes above ${maxFontSizePx}px are unavailable with ${columnCount} columns — each value must fit its cell. Remove columns to unlock larger sizes.`
        : "";

    if (explicitPx === undefined) {
      return `Auto uses ${autoPx}px for ${columnCount} column${columnCount === 1 ? "" : "s"} and shrinks as columns are added.${capNote}`;
    }

    return `Applies to the header and line items. Text wraps within its cell.${capNote}`;
  }, [allValues.font_size, tableColumns.length, maxFontSizePx]);

  // Calculate selected counts for each tab
  const generalCount = useMemo(() => {
    const generalFields = [
      "include_hearing_aids",
      "include_domes",
      "include_receivers",
      "include_tubes",
      "include_earmolds",
      "include_batteries",
      "include_chargers",
      "include_remotes",
      "include_others",
      "include_services_dispensing_fees",
      "include_totals"
    ];
    return generalFields.filter(
      (field) => allValues?.[field as keyof typeof allValues]
    ).length;
  }, [allValues]);

  const devicesCount = useMemo(() => {
    const devicePrefixes = [
      "hearing_aids",
      "domes",
      "receivers",
      "tubes",
      "earmolds",
      "batteries",
      "chargers",
      "remotes",
      "others"
    ];

    if (!allValues) return 0;

    return Object.entries(allValues).filter(([key]) => {
      return (
        devicePrefixes.some((prefix) => key.startsWith(`${prefix}_show_`)) &&
        Boolean(allValues[key as keyof SelectSaleConfigurationValues])
      );
    }).length;
  }, [allValues]);

  const servicesCount = useMemo(() => {
    const servicesFields = [
      "services_show_cpt_code",
      "services_show_product",
      "services_show_description",
      "services_show_price",
      "services_show_quantity"
    ];
    return servicesFields.filter(
      (field) => allValues?.[field as keyof typeof allValues]
    ).length;
  }, [allValues]);

  const totalsCount = useMemo(() => {
    const totalsFields = [
      "total_show_services_dispensing_fees",
      "total_show_discounts",
      "total_tax",
      "total_amount",
      "total_patient_payments",
      "total_insurance_payments",
      "amount_due_from_insurance",
      "amount_due_from_patient",
      "balance_due",
      "total_show_cost_of_ha"
    ];
    return totalsFields.filter(
      (field) => allValues?.[field as keyof typeof allValues]
    ).length;
  }, [allValues]);

  const handleClose = useCallback(() => {
    reset(initialValuesRef.current);

    dispatch(setDataParams({ id, openSelectSaleConfigurationDialog: false }));
    onTagDone?.();
  }, [dispatch, id, reset, onTagDone]);

  const saleId = dataParams?.saleId;

  const handleSave = useCallback(
    async (data: SelectSaleConfigurationValues) => {
      // Nothing changed: the tag keeps its exact text. Written back, a tag that left a section's columns out (as
      // converted tags and tables without totals do) would gain them, printing the same but changing the template.
      if (tagRequest?.mode === "edit" && isEqual(data, tagValuesRef.current)) {
        handleClose();
        return;
      }

      const result = tagOptionsFromFormValues(data, {
        fontSizeEnabled: isTableFontSizeEnabled
      });
      // With the size pickers switched off they can't be changed here, so an edited tag keeps its own sizes rather
      // than going back to auto (a plain Define Invoice Parameters keeps doing that, as it always has).
      if (tagRequest && !isTableFontSizeEnabled) {
        (["font_size", "totals_font_size"] as const).forEach((key) => {
          if (tagRequest.options[key] != null) {
            result[key] = tagRequest.options[key];
          }
        });
      }

      dispatch(
        setDataParams({
          id,
          saleId: Number(saleId),
          saleConfiguration: result
        })
      );
      if (tagRequest) onTagApply?.(result);
      handleClose();
    },
    [
      dispatch,
      handleClose,
      id,
      saleId,
      isTableFontSizeEnabled,
      tagRequest,
      onTagApply
    ]
  );

  const handleRemoveTag = useCallback(() => {
    onTagRemove?.();
    handleClose();
  }, [onTagRemove, handleClose]);

  // An inserted or edited table must print something: an empty one would leave a tag that prints nothing.
  const isEmptyTable =
    Boolean(tagMode) && !tableColumns.length && !allValues.include_totals;

  const [value, setValue] = useState("general");

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };

  // Accordion expanded states
  const [generalExpanded, setGeneralExpanded] = useState(true);
  const [servicesExpanded, setServicesExpanded] = useState(true);
  const [totalsExpanded, setTotalsExpanded] = useState(true);
  const [deviceExpandedStates, setDeviceExpandedStates] = useState<
    Record<string, boolean>
  >({
    hearing_aids: true,
    domes: true,
    receivers: true,
    tubes: true,
    earmolds: true,
    batteries: true,
    chargers: true,
    remotes: true,
    others: true
  });

  const handleGeneralAccordionChange = useCallback(
    (event: React.SyntheticEvent, expanded: boolean) => {
      setGeneralExpanded(expanded);
    },
    []
  );

  const handleServicesAccordionChange = useCallback(
    (event: React.SyntheticEvent, expanded: boolean) => {
      setServicesExpanded(expanded);
    },
    []
  );

  const handleTotalsAccordionChange = useCallback(
    (event: React.SyntheticEvent, expanded: boolean) => {
      setTotalsExpanded(expanded);
    },
    []
  );

  const handleExpandedStateChange = useCallback(
    (key: string, expanded: boolean) => {
      if (key === "services") {
        setServicesExpanded(expanded);
      } else if (key === "totals") {
        setTotalsExpanded(expanded);
      } else {
        setDeviceExpandedStates((prev) => ({
          ...prev,
          [key]: expanded
        }));
      }
    },
    []
  );

  const handleDeviceExpandedChange = useCallback(
    (namePrefix: string) =>
      (event: React.SyntheticEvent, expanded: boolean) => {
        setDeviceExpandedStates((prev) => ({
          ...prev,
          [namePrefix]: expanded
        }));
      },
    []
  );

  return (
    <Dialog
      title={
        tagMode
          ? TAG_TITLES[tagMode]
          : "Please Define Invoice Merge Tag Parameters"
      }
      onClose={handleClose}
      open={isOpen}
      minWidth={770}
      dialogContentProps={{
        sx: {
          display: "flex",
          alignItems: "center",
          pb: "20px",
          flexDirection: "column",
          background: neutral[50]
        }
      }}
      sx={{ zIndex: zIndex.snackbar + 3 }}
    >
      {tagMode && (
        <Typography variant="body1" width="100%" mb={1}>
          {TAG_INTROS[tagMode]}
        </Typography>
      )}
      <Stack height={710} maxHeight={710} width="100%" mb={1} gap={1}>
        <TabContext value={value}>
          <TabList
            onChange={handleChange}
            sx={{
              "& .MuiTabs-flexContainer": {
                overflowX: "auto",
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": {
                  display: "none"
                },
                WebkitOverflowScrolling: { xs: "touch", md: "auto" }
              }
            }}
          >
            <Tab
              label={
                <Badge badgeContent={generalCount} color="primary">
                  General Settings
                </Badge>
              }
              value="general"
            />
            <Tab
              label={
                <Badge badgeContent={devicesCount} color="primary">
                  Device Settings
                </Badge>
              }
              value="devices"
            />
            <Tab
              label={
                <Badge badgeContent={servicesCount} color="primary">
                  Services Settings
                </Badge>
              }
              value="services"
            />
            <Tab
              label={
                <Badge badgeContent={totalsCount} color="primary">
                  Totals Settings
                </Badge>
              }
              value="totals"
            />
          </TabList>

          <FormProvider {...formMethods}>
            <Stack
              id="settings-form"
              data-testid="settings-form"
              sx={{
                height: "100%",
                overflow: "auto",
                "&::-webkit-scrollbar": {
                  width: "8px"
                },
                "&::-webkit-scrollbar-track": {
                  background: "#f1f1f1",
                  borderRadius: "4px"
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#c1c1c1",
                  borderRadius: "4px",
                  "&:hover": {
                    background: "#a8a8a8"
                  }
                }
              }}
            >
              <TabPanel value="general">
                <GeneralSettings
                  expanded={generalExpanded}
                  onChange={handleGeneralAccordionChange}
                  onExpandedStateChange={handleExpandedStateChange}
                />
              </TabPanel>
              <TabPanel value="devices">
                <DeviceSettings
                  expandedStates={deviceExpandedStates}
                  onExpandedChange={handleDeviceExpandedChange}
                />
              </TabPanel>
              <TabPanel value="services">
                <ServicesSettings
                  expanded={servicesExpanded}
                  onChange={handleServicesAccordionChange}
                />
              </TabPanel>
              <TabPanel value="totals">
                <TotalsSettings
                  expanded={totalsExpanded}
                  onChange={handleTotalsAccordionChange}
                />
              </TabPanel>
            </Stack>
          </FormProvider>
        </TabContext>
        <Stack mb={1.5}>
          <Typography variant="body3" mt={1} mb={1} sx={{ fontSize: 20 }}>
            Table columns
          </Typography>
          <Stack direction="row" columnGap={1} rowGap={0.5} flexWrap="wrap">
            {(tableColumns.length ? tableColumns : ["No columns"]).map(
              (column) => (
                <Typography
                  key={column}
                  variant="body1"
                  sx={{
                    background: neutral[100],
                    padding: "4px",
                    borderRadius: "4px",
                    color: neutral[800]
                  }}
                >
                  {column}
                </Typography>
              )
            )}
          </Stack>
          {hasNameColumn && (
            <Stack mt={1.5}>
              <FormControlLabel
                label='Head the Name column "Model" and print it after Manufacturer'
                control={
                  <CheckboxFormField
                    name="name_as_model"
                    control={formMethods.control}
                  />
                }
              />
              <Typography variant="body2" color="text.secondary">
                The table then has one Model column. Rows that show a Name print
                the name from your Catalog; rows that show only a Model print
                the model.
              </Typography>
            </Stack>
          )}
          {isTableFontSizeEnabled && (
            <Stack direction="row" alignItems="flex-start" columnGap={2} mt={2}>
              <SelectFormField
                name="font_size"
                label="Table font size"
                control={formMethods.control}
                options={fontSizeOptions}
                required
                dropDownZIndex={zIndex.snackbar + 4}
                helperText={fontSizeHelperText}
                sx={{ maxWidth: 320 }}
              />
              {allValues.include_totals && (
                <SelectFormField
                  name="totals_font_size"
                  label="Totals font size"
                  control={formMethods.control}
                  options={TOTALS_FONT_SIZE_OPTIONS}
                  required
                  dropDownZIndex={zIndex.snackbar + 4}
                  helperText={
                    TOTALS_FONT_SIZE_HELPER_TEXT[allValues.totals_font_size] ??
                    "Totals print at this size. The line items keep their own."
                  }
                  sx={{ maxWidth: 320 }}
                />
              )}
            </Stack>
          )}
          <Typography variant="body3" mt={2} mb={0} sx={{ fontSize: 20 }}>
            {allValues.include_totals ? "Totals Included" : "No totals"}
          </Typography>
          {isEmptyTable && (
            <Typography variant="body1" color="error.main" mt={1}>
              Choose at least one section under General Settings, or include
              totals, so the table prints something.
            </Typography>
          )}
        </Stack>
      </Stack>
      <DialogActions>
        {tagMode === "edit" && onTagRemove && (
          // A plain outlined button, as Cancel: the theme draws every outlined button in primary, and removing is
          // undone with Ctrl+Z in the editor, so it needs no danger treatment or confirmation.
          <Button
            variant="outlined"
            size="small"
            onClick={handleRemoveTag}
            style={{ background: "white" }}
          >
            Remove Table
          </Button>
        )}
        <Button
          variant="outlined"
          size="small"
          onClick={handleClose}
          style={{ background: "white" }}
        >
          Cancel
        </Button>

        <LoadingButton
          disabled={!isValid || isEmptyTable}
          variant="contained"
          size="small"
          onClick={handleSubmit(handleSave)}
        >
          {tagMode ? TAG_APPLY_LABELS[tagMode] : "Apply Parameters"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
